import { isEmpty, mergeWith } from 'lodash';
import { useEffect, useCallback } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Label } from '@hivebrite/design-system/Label';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import { concatArraysCustomizer } from 'hb-react/shared/utils/mergeWith';

import { useGetTags } from './hooks/useTags';
import TagsFilters from './TagsFilters';

const BlockTagsFilters = ({
  blockId,
  blockQuery,
  canToggle,
  dataTestId,
  fetchTags,
  hideSingleTag,
  label,
  limit, // Will be ignored if singleRow is true below
  multi,
  queryId,
  setBlockData,
  setBlockQuery,
  showAllTag,
  singleRow: singleRowOverride,
}) => {
  const breakpoint = useBreakpoint();
  const tags = useGetTags({ fetchTags, queryId });

  const singleRow = singleRowOverride ?? ['s', 'm'].includes(breakpoint);

  const selectedTags = Object.keys(blockQuery ?? {});

  useEffect(() => {
    setBlockData(isEmpty(blockQuery) ? null : blockQuery);
  }, [setBlockData, blockQuery]);

  const handleTagClick = useCallback(
    (newTags) => {
      setBlockQuery(
        tags
          .filter(({ name }) => newTags.includes(name))
          .reduce(
            (result, { query: tagQuery }) =>
              mergeWith(null, result, tagQuery, concatArraysCustomizer),
            null,
          ),
      );
    },
    [setBlockQuery, tags],
  );

  if (tags?.length <= (hideSingleTag ? 1 : 0)) {
    return null;
  }

  return (
    <Box display="block" data-testid={dataTestId}>
      {label ? (
        <Box mb={2}>
          <Label
            id={blockId}
            multiline={false}
            position="top"
            required={false}
            noHtmlFor
          >
            {label}
          </Label>
        </Box>
      ) : (
        <ScreenReadersContent id={blockId}>
          {s__('Accessibility|Filter by category')}
        </ScreenReadersContent>
      )}
      <TagsFilters
        tags={tags}
        selectedTags={selectedTags}
        canToggle={canToggle}
        multi={multi}
        showAllTag={showAllTag}
        limit={singleRow ? null : limit}
        onClick={handleTagClick}
        singleRow={singleRow}
        labelledBy={blockId}
      />
    </Box>
  );
};

BlockTagsFilters.defaultProps = {
  multi: false,
};

export default BlockTagsFilters;
